import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';
import { API_INSTANCE, buildURLWithState } from './utils';
import { Site, TeamMemberType } from 'src/types/types';

export type SubscriptionSliceState = {
  subscription: any;
  plans: any;
  currentWidget: any;
  widgets: any;
  team: any;
  site: Site | null;
  allUserSites: Site[] | null;
  loading: boolean;
  siteLoading: boolean;
  wixSite: Site | null;
  error?: boolean;
};

const initialState: SubscriptionSliceState = {
  subscription: null,
  plans: [],
  widgets: [],
  team: [],
  site: null,
  allUserSites: [],
  loading: false,
  siteLoading: false,
  currentWidget: null,
  wixSite: null,
  error: false,
};

export const fetchWixSite = createAsyncThunk('site/fetch-wix-site', async (_, { getState }) => {
  return API_INSTANCE.get<any>(buildURLWithState(getState(), `/api/v1/dashboard/wix`))
    .then((response) => response.data)
    .catch((error) => error);
});

export const fetchAccountSites = createAsyncThunk('site/fetch-sites', async (_, { getState }) => {
  return API_INSTANCE.get<any>(buildURLWithState(getState(), `/api/v1/dashboard/sites`))
    .then((response) => response.data)
    .catch((error) => error);
});
export const fetchSubscriptionApi = createAsyncThunk(
  'site/fetch-subscription',
  async (siteId, { getState }) => {
    return API_INSTANCE.get<any>(
      buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/subscription`),
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);
export const fetchWixSubscriptionApi = createAsyncThunk(
  'site/fetch-wix-subscription',
  async (instance, { getState }) => {
    return API_INSTANCE.get<any>(
      buildURLWithState(
        getState(),
        `/api/v1/dashboard/sites/channels/wix/subscription`,
        `&brand=wix&instance=${instance}`,
      ),
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);

export const updateSiteSession = createAsyncThunk(
  'site/update-session',
  async (siteId: string, { getState }) => {
    return API_INSTANCE.put<any>(
      buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/session`),
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);

export const fetchTeamMembers = createAsyncThunk(
  'site/fetch-team',
  async (siteId: string, { getState }) => {
    return API_INSTANCE.get<TeamMemberType>(
      buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/teams`),
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);
export const updateTeamMember = createAsyncThunk(
  'site/update-team-member',
  async ({ siteId, data }: { siteId: string; data: Record<string, unknown> }, { getState }) => {
    return API_INSTANCE.put<any>(
      buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/teams/${data.id}`),
      data,
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);
export const deleteTeamMember = createAsyncThunk(
  'site/delete-team-member',
  async ({ siteId, id }: { siteId: string; id: string }, { getState }) => {
    return API_INSTANCE.delete<any>(
      buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/teams/${id}`),
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);
export const createTeamMember = createAsyncThunk(
  'site/create-team-member',
  async ({ siteId, data }: { siteId: string; data: Record<string, unknown> }, { getState }) => {
    return API_INSTANCE.post<any>(
      buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/teams`),
      data,
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);

export const resendTeamMemberInvitation = createAsyncThunk(
  'site/resend-team-member-invitation',
  async ({ siteId, memberId }: { siteId: string; memberId: string }, { getState }) => {
    return API_INSTANCE.post<any>(
      buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/teams/${memberId}/resend`),
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);

export const fetchSite = createAsyncThunk('sites/fetch-by-id', async (siteId: string) => {
  return API_INSTANCE.get<any>(`/api/v1/dashboard/sites/${siteId}`)
    .then((response) => response.data)
    .catch((error) => error);
});

export const fetchSiteWithParams = createAsyncThunk(
  'sites/fetch-site-with-params',
  async (siteId, { getState }) => {
    return API_INSTANCE.get<any>(`/api/v1/dashboard/sites/`)
      .then((response) => response.data)
      .catch((error) => error);
  },
);

export const fetchAllUserSites = createAsyncThunk('sites/fetch-all', async () => {
  return API_INSTANCE.get<any>(`/api/v1/dashboard/sites`)
    .then((response) => response.data)
    .catch((error) => error);
});

export const createNewSite = createAsyncThunk(
  'sites/create',
  async ({ name, url, type }: { name: string; url: string; type: string }) => {
    return API_INSTANCE.post<Site>(`/api/v1/dashboard/sites`, { name, url, type })
      .then((response) => response.data)
      .catch((error) => error);
  },
);

export const verifySiteDomain = createAsyncThunk(
  'sites/verify-domain',
  async (data, { getState }) => {
    return API_INSTANCE.post<any>(`/api/v1/dashboard/sites/${data.siteId}/verify-events-domain`, {
      custom_domain: data.customDomain,
    })
      .then((response) => response.data)
      .catch((error) => error);
  },
);

export const verifyDashboardDomain = createAsyncThunk(
  'sites/verify-dashboard-domain',
  async (data, { getState }) => {
    return API_INSTANCE.post<any>(
      `/api/v1/dashboard/sites/${data.siteId}/verify-dashboard-domain`,
      {
        custom_dashboard_domain: data.customDashboardDomain,
      },
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);

export const fetchWidgetsApi = createAsyncThunk('widgets/fetch', async (siteId, { getState }) => {
  return API_INSTANCE.get<any>(
    buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/widgets`),
  )
    .then((response) => response.data)
    .catch((error) => error);
});
export const fetchWidgetApi = createAsyncThunk(
  'widget/fetch',
  async ({ siteId, widgetId }: { siteId: string; widgetId: string }, { getState }) => {
    return API_INSTANCE.get<any>(
      buildURLWithState(getState(), `/api/v1/dashboard/sites/${siteId}/widgets/${widgetId}`),
    )
      .then((response) => response.data)
      .catch((error) => error);
  },
);
export const deleteWidgetsApi = createAsyncThunk('widgets/delete', async (widget, { getState }) => {
  return API_INSTANCE.delete<any>(
    buildURLWithState(getState(), `/api/v1/dashboard/sites/${widget.site_id}/widgets/${widget.id}`),
  )
    .then((response) => response.data)
    .catch((error) => error);
});
export const createWidgetsApi = createAsyncThunk('widgets/create', async (widget, { getState }) => {
  return API_INSTANCE.post<any>(
    buildURLWithState(getState(), `/api/v1/dashboard/sites/${widget.site_id}/widgets`),
    widget,
  )
    .then((response) => response.data)
    .catch((error) => error);
});
export const updateWidgetsApi = createAsyncThunk('widgets/update', async (widget, { getState }) => {
  return API_INSTANCE.put<any>(
    buildURLWithState(getState(), `/api/v1/dashboard/sites/${widget.site_id}/widgets/${widget.id}`),
    widget,
  )
    .then((response) => response.data)
    .catch((error) => error);
});
export const fetchBilling = createAsyncThunk('site/fetch-billing', async (_, { getState }) => {
  return API_INSTANCE.get<any>(buildURLWithState(getState(), `/api/v1/dashboard/billing`))
    .then((response) => response.data)
    .catch((error) => error);
});
export const fetchPlans = createAsyncThunk('site/fetch-plans', async (_, { getState }) => {
  return API_INSTANCE.get<any>(
    buildURLWithState(getState(), `/api/v1/dashboard/billing/public/plans`),
  )
    .then((response) => response.data)
    .catch((error) => error);
});

export const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    config_success: (state, action: PayloadAction<any>) => {
      state.data = action.payload;
    },
    set_selected_site: (state, action: PayloadAction<Site>) => {
      state.site = action.payload;
    },
    update_site: (state, action: PayloadAction<any>) => {
      state.allUserSites = state.allUserSites.map((site) => {
        if (site.id === action.payload.siteId) {
          return { ...site, ...action.payload.updatedSiteInfo };
        }
        return site;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSubscriptionApi.fulfilled, (state, action) => {
      state.subscription = action.payload;
    });
    builder.addCase(fetchWixSubscriptionApi.fulfilled, (state, action) => {
      state.subscription = action.payload;
    });
    builder.addCase(fetchTeamMembers.fulfilled, (state, action) => {
      state.team = action.payload;
    });
    builder.addCase(fetchBilling.fulfilled, (state, action) => {
      state.plans = action.payload;
    });
    builder.addCase(fetchPlans.fulfilled, (state, action) => {
      state.plans = action.payload;
    });
    builder.addCase(fetchWidgetsApi.fulfilled, (state, action) => {
      state.widgets = action.payload;
    });
    builder.addCase(fetchWidgetApi.fulfilled, (state, action) => {
      state.currentWidget = action.payload;
    });
    builder.addCase(deleteWidgetsApi.fulfilled, (state, action) => {
      state.widgets = state.widgets.filter((w) => w.id !== action?.meta?.arg?.id);
    });
    builder.addCase(deleteTeamMember.fulfilled, (state, action) => {
      state.team = state.team.filter((w) => w.id !== action?.meta?.arg?.id);
    });
    builder.addCase(createWidgetsApi.fulfilled, (state, action) => {
      state.widgets = [{ ...action.payload, is_new: true }].concat(state.widgets);
    });
    builder.addCase(createTeamMember.fulfilled, (state, action) => {
      state.team = state.team.concat(action.payload);
    });
    builder.addCase(updateTeamMember.fulfilled, (state, action) => {
      state.team = state.team.map((team) =>
        action.meta.arg.data.id === team.id ? { ...team, ...action.payload } : team,
      );
    });
    builder.addCase(fetchSite.pending, (state, action) => {
      state.siteLoading = false;
    });
    builder.addCase(fetchSite.fulfilled, (state, action) => {
      state.site = action.payload;
      state.siteLoading = true;
    });
    builder.addCase(fetchAllUserSites.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAllUserSites.fulfilled, (state, action) => {
      state.allUserSites = action.payload;
      state.error = false;
      state.loading = false;
    });
    builder.addCase(createNewSite.fulfilled, (state, action) => {
      state.allUserSites = [...state.allUserSites, action.payload];
    });
    builder.addCase(fetchAllUserSites.rejected, (state) => {
      state.error = true;
      state.loading = false;
    });
    builder.addCase(verifySiteDomain.fulfilled, (state, action) => {
      if (action.payload.id) {
        state.site = action.payload;
      } else {
        throw new Error('Could not verify domain');
      }
    });
    builder.addCase(verifyDashboardDomain.fulfilled, (state, action) => {
      if (action.payload.id) {
        state.site = action.payload;
      } else {
        throw new Error('Could not verify dashboard domain');
      }
    });
    builder.addCase(fetchWixSite.fulfilled, (state, action) => {
      state.site = action.payload;
    });
    builder.addCase(updateWidgetsApi.fulfilled, (state, action) => {
      state.widgets = state.widgets.map((w) => {
        const updatedWidget = action?.meta?.arg;
        if (w.id === updatedWidget.id) {
          return { ...w, ...updatedWidget };
        }
        return w;
      });
    });
  },
});

export const selectSubscription = (state: RootState) => state?.sites.subscription;
export const selectPlans = (state: RootState) => state?.sites.plans;
export const selectWidgets = (state: RootState) => state?.sites.widgets;
export const selectCurrentWidget = (state: RootState) => state?.sites.currentWidget;
export const selectSiteTeam = (state: RootState) => state?.sites.team;
export const selectSite = (state: RootState) => state?.sites.site;
export const selectSiteLoading = (state: RootState) => state?.sites.siteLoading;
export const allUserSites = (state: RootState) => state?.sites.allUserSites;
export const allUserSitesFetchError = (state: RootState) => state.sites.error;
export const allUserSitesLoading = (state: RootState) => state?.sites.loading;

export default sitesSlice;
